import React, { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import "../../styles/Components/sidebar.css"
import { menuItems, settingsItems, organizationMenuItems } from "../../utils/constants"
import { getUserCookies } from "../../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import { translations } from "../../utils/Translation/staticTranslation"
import {
  getAllowedModuleKeysFromProfile,
  menuItemMatchesAllowedModules,
  effectiveHighestRole,
  resolveActiveSidebarPath,
  userHasScopedModulePermissions,
} from "../../utils/modulePermissionSidebar"
import {
  canAccessOrganizationRoutes,
  hasMerchantPortalAccess,
  isOrgOnlyOwnerUser,
} from "../../utils/organizationAccess"
import {
  getLocalizedPath,
  stripEnglishPrefix,
} from "../../utils/authLanguageRoutes"
import { getAppLanguage } from "../../utils/getAppLanguage"

const Sidebar = () => {
  const user = useSelector((state) => state.user.user)
  const isOrgOwner = useSelector((state) => state.organization.isOwner)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const marketRoleStr =
    decoded?.market_role != null ? String(decoded.market_role) : ""
  const effectiveHighest = effectiveHighestRole(user, highest_role)
  const isAccountSuperAdmin =
    String(highest_role ?? "").toUpperCase() === "SUPERADMIN" ||
    decoded?.is_superuser === true ||
    decoded?.is_super_user === true ||
    user?.is_superuser === true ||
    user?.is_super_user === true ||
    user?.market_role_permissions?.is_superuser === true

  const navigate = useNavigate()
  const location = useLocation()
  const [hoveredItem, setHoveredItem] = useState(null)

  const allowedModules = getAllowedModuleKeysFromProfile(user)
  const useProfileModuleFilter = userHasScopedModulePermissions(user)
  const merchantPortalAccess = hasMerchantPortalAccess(user, token, isOrgOwner)

  const filteredMenuItems = menuItems.filter((item) => {
    if (user && !merchantPortalAccess) return false

    if (item.name === "Zory Analytics") {
      return isAccountSuperAdmin
    }

    if (useProfileModuleFilter && allowedModules) {
      if (item.name === "Admin roles") {
        return (
          allowedModules.has("create-roles") &&
          effectiveHighest === "SUPERADMIN"
        )
      }
      return menuItemMatchesAllowedModules(item, allowedModules)
    }

    if (marketRoleStr === "2") {
      const allowedItems = ["Merchants", "Stores", "Merchant Users"]
      return allowedItems.includes(item.name)
    }

    if (marketRoleStr === "3") {
      const allowedItems = [
        "Dashboard",
        "Merchants",
        "Stores",
        "Merchant Users",
      ]
      return allowedItems.includes(item.name)
    }

    if (marketRoleStr === "1") {
      const allowedItems = [
        "Dashboard",
        "PostHog",
        "Users",
        "Campaigns",
        "Discounts",
        "Announcements",
        "Contacts",
      ]
      return allowedItems.includes(item.name)
    }

    if (item.name === "Admin roles" && effectiveHighest !== "SUPERADMIN") {
      return false
    }

    // Reached only by users without scoped module permissions — those are
    // handled by menuItemMatchesAllowedModules above, which honours a
    // `sales_requests` grant. Everyone else here must be a superadmin.
    if (
      (item.name === "Sales Requests" || item.name === "Policies & Privacy") &&
      effectiveHighest !== "SUPERADMIN"
    ) {
      return false
    }

    if (effectiveHighest === "SUPERADMIN" && item.name === "Products") {
      return false
    }
    const isMerchantPortalJwt =
      !allowedModules &&
      (highest_role === "OWNER" ||
        highest_role === "ADMIN" ||
        highest_role === "VIEWER")
    if (
      isMerchantPortalJwt &&
      (item.name === "Merchants" ||
        item.name === "PostHog" ||
        item.name === "Library" ||
        item.name === "Categories" ||
        item.name === "Contacts" ||
        item.name === "Users" ||
        item.name === "Campaigns" ||
        item.name === "Discounts" ||
        item.name === "Plan and Pricing" ||
        item.name === "Plan Usage" ||
        item.name === "Referrals" ||
        item.name === "Announcements" ||
        item.name === "Chatbot Checker" ||
        item.name === "Sales Requests" ||
        item.name === "Policies & Privacy" ||
        item.name === "Admin roles")
    ) {
      return false
    }
    return true
  })

  const showOrganizationSection = canAccessOrganizationRoutes(user, isOrgOwner)
  const orgOnlyOwner = isOrgOnlyOwnerUser(user, isOrgOwner, token)

  // Settings (profile / password) is available to every portal user and role —
  // not gated by module permissions or market_role.
  const filteredSettingsItems = settingsItems

  const showSettingsSection = Boolean(user) && (merchantPortalAccess || orgOnlyOwner)

  const lang = getAppLanguage()
  const basePath = stripEnglishPrefix(location.pathname)
  const activeSegments = basePath.split("/").filter(Boolean)

  const navigateLocalized = (path) => {
    const normalized = String(path || "").startsWith("/")
      ? String(path)
      : `/${path}`
    navigate(getLocalizedPath(normalized, lang))
  }

  const getLocalizedExternalUrl = (url) => {
    const parsed = new URL(url)
    const segments = parsed.pathname.split("/").filter(Boolean)
    if (segments[0] === "en" || segments[0] === "ar") {
      segments[0] = lang
    } else {
      segments.unshift(lang)
    }
    parsed.pathname = `/${segments.join("/")}${segments.length ? "/" : ""}`
    return parsed.toString()
  }

  // Routes are registered both with and without a locale prefix
  // (e.g. "/posthog" for Arabic and "/en/posthog" for English), so the first
  // path segment is the locale on English URLs. Strip it before matching, or
  // the active tab never highlights on "/en/*" routes.
  const LOCALE_PREFIXES = ["en", "ar"]
  const pathSegments = location.pathname.split("/").filter(Boolean)
  const routeSegments = LOCALE_PREFIXES.includes(pathSegments[0])
    ? pathSegments.slice(1)
    : pathSegments
  const currentRoutePath = `/${routeSegments.join("/")}`

  // Exactly one entry is active at a time — see resolveActiveSidebarPath, which
  // absorbed the old per-item and organization-specific matching.
  const activeSidebarPath = resolveActiveSidebarPath(routeSegments, [
    ...menuItems,
    ...organizationMenuItems,
  ])

  const renderMenuItem = (item) => {
    const isActive =
      String(item.path || "").replace(/^\//, "") === activeSidebarPath
    const isHovered = hoveredItem === item.path

    return (
      <div
        key={item.path}
        className={`d-flex align-items-center mb-32 pointer sidebar-item ${
          isActive ? "text-FB6514" : "text-3F3F3F"
        }`}
        onClick={() => {
          if (item.ssoUrl) {
            const currentToken = getUserCookies()?.token
            if (!currentToken) return

            const form = document.createElement("form")
            form.method = "POST"
            form.action = item.ssoUrl

            const input = document.createElement("input")
            input.type = "hidden"
            input.name = "token"
            input.value = currentToken
            form.appendChild(input)

            const languageInput = document.createElement("input")
            languageInput.type = "hidden"
            languageInput.name = "lang"
            languageInput.value = localStorage.getItem("language")
            form.appendChild(languageInput)

            document.body.appendChild(form)
            form.submit()
            return
          }

          if (item.externalUrl) {
            window.location.href = getLocalizedExternalUrl(item.externalUrl)
            return
          }

          if (item.name === "Users") {
            navigateLocalized(item.path)
            window.location.reload()
          } else {
            navigateLocalized(item.path)
          }
        }}
        onMouseEnter={() => setHoveredItem(item.path)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        {item.icon ? (
        <img
          src={`${
            item.name === "Admin roles" || item.name === "Organization team"
              ? `/icons/${
                  isHovered || isActive
                    ? item.activeIcon || item.hoverIcon
                    : item.icon
                }`
              : item.name === "Campaigns"
              ? `/Campaign/${
                  isHovered
                    ? item.hoverIcon
                    : isActive
                    ? item.activeIcon
                    : item.icon
                }`
              : item.name === "Referrals"
              ? `/referrals/${
                  isHovered
                    ? item.hoverIcon
                    : isActive
                    ? item.activeIcon
                    : item.icon
                }`
              : item.name === "Discounts"
              ? `/discounts/${
                  isHovered
                    ? item.hoverIcon
                    : isActive
                    ? item.activeIcon
                    : item.icon
                }`
              : item.name === "Organization generations"
                ? `/dashboard/${
                    isHovered || isActive ? item.activeIcon : item.icon
                  }`
              : `/Sidebar/${
                  isHovered
                    ? item.hoverIcon
                    : isActive
                    ? item.activeIcon
                    : item.icon
                }`
          }`}
          alt=""
          className="mr-12"
          style={
            item.name === "Discounts"
              ? {
                  width: "20px",
                  height: "20px",
                  objectFit: "contain",
                  flexShrink: 0,
                }
              : {}
          }
        />
        ) : null}
        <div
          className={`text-13 sidebar-item ${
            lang === "ar" ? "mr-12" : ""
          } `}
        >
          {translations[lang]?.Sidebar[item.name]}
        </div>
      </div>
    )
  }

  return (
    <aside className="bg-FFFFFF sidebar-container">
      <div className="d-flex flex-column h-100">
        {/* Logo stays pinned; only the menu list scrolls, so a long menu never
            pushes the settings section out of the viewport. */}
        <div className="sidebar-logo-block text-center">
          <img src="/logo/logo.svg" alt="logo" width={135} height={30} />
        </div>

        <div className="sidebar-menu-scroll">
          {!orgOnlyOwner
            ? filteredMenuItems.map((item) => renderMenuItem(item))
            : null}

          {showOrganizationSection
            ? organizationMenuItems.map((item) => renderMenuItem(item))
            : null}
        </div>

        {showSettingsSection && (
          <div className="border-top-E4E5EC sidebar-settings-block">
            <div className="plan-setting-section">
              {filteredSettingsItems.map((item) => {
                const isActive = currentRoutePath === item.path
                const isHovered = hoveredItem === item.path

                return (
                  <div
                    key={item.path}
                    className={`d-flex align-items-center pointer sidebar-item mb-24 ${
                      isActive ? "text-FB6514" : "text-3F3F3F"
                    }`}
                    onClick={() => navigateLocalized(item.path)}
                    onMouseEnter={() => setHoveredItem(item.path)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <img
                      src={`/Sidebar/${
                        isHovered
                          ? item.hoverIcon
                          : isActive
                          ? item.activeIcon
                          : item.icon
                      }`}
                      alt=""
                      className="mr-12"
                    />
                    <div
                      className={`text-13 sidebar-item  ${
                        lang === "ar" ? "mr-12" : ""
                      }`}
                    >
                      {translations[lang]?.Sidebar[item.name]}
                    </div>
                  </div>
                )
              })}

              <p className="text-12 text-8C8C80">
                {" "}
                {lang === "en"
                  ? `Copyright © 2025 Zory`
                  : `جميع الحقوق محفوظة ©  زوري  2024`}
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}

export default Sidebar
